import React, { useEffect, useState } from "react";
import DecorativeSquares from "../../components/ui/DecorativeSquares"; 
import { ChevronRight, PhoneCall, Mail, FilePenLine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { getInquirys } from "../../redux/thunks/profileThunk";
import Pagination from "../../components/Pagination";
import ImagePreview from "../../components/ui/ImagePreview";
import { FaArrowLeft } from "react-icons/fa";

const Inquiry = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [allInquiry, setAllInquiry] = useState([]);
  const [openIndex, setOpenIndex] = useState(null);
  const { isLoading, pagination } = useSelector((state) => state.profile);

  const { current_page, total_pages, total_records } = pagination;

  useEffect(() => {
    dispatch(
      getInquirys({
        page,
      }),
    )
      .unwrap()
      .then((res) => {
        setAllInquiry(res?.data?.inquiries || []);
      });
  }, [dispatch, page]);

  /* ================= UTILITIES ================= */

  const getInitials = (name = "") => {
    const words = name.trim().split(" ").filter(Boolean);

    if (words.length === 0) return "";

    // If only one word → return first 2 letters
    if (words.length === 1) {
      return words[0].slice(0, 2).toUpperCase();
    }

    // If multiple words → first letter of first & second word
    return (words[0].charAt(0) + words[1].charAt(0)).toUpperCase();
  };

  const formatDateTime12 = (dateTime) => {
    if (!dateTime) return "";
    const date = new Date(dateTime.replace(" ", "T"));
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  /* ================= UI ================= */

  return (
    <section className="relative max-w-7xl mx-auto px-4 py-6 pb-28">
      <DecorativeSquares />

      {/* ================= HEADER ================= */}
      <div className="sticky top-2 z-10 bg-white/80 backdrop-blur-md rounded-2xl shadow-md px-4 py-3 flex items-center justify-between mb-6">
        <button
          onClick={() => navigate("/profile")}
          className="p-2 rounded-full hover:bg-gray-100 transition"
        >
          <FaArrowLeft size={20} /> 
        </button>

        <h1 className="text-lg md:text-xl font-semibold text-gray-900">
          Past Inquiries
        </h1>

        <div className="w-9" />
      </div>

      {/* ================= LIST ================= */}
      <div className="space-y-4">
        {isLoading ? (
          Array(5)
            .fill(0)
            .map((_, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* avatar */}
                    <div className="h-8 w-10 rounded-md shimmer"></div>

                    {/* name + phone */}
                    <div className="space-y-2">
                      <div className="h-3 w-32 shimmer rounded"></div>
                      <div className="h-3 w-24 shimmer rounded"></div>
                    </div>
                  </div>

                  {/* arrow */}
                  <div className="h-5 w-5 shimmer rounded"></div>
                </div>
              </div>
            ))
        ) : allInquiry.length > 0 ? (
          allInquiry.map((item, index) => {
            const isOpen = openIndex === index;

            return (
              <div
                key={item.id}
                className="bg-white rounded-2xl shadow-sm border border-gray-100 transition hover:shadow-md"
              >
                {/* ===== HEADER ROW ===== */}
                <button
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  className="w-full flex items-center justify-between px-4 py-4"
                >
                  <div className="flex items-center gap-4">
                    {/* AVATAR */}
                    <div className="px-2 py-1 border border-[#FFCCBF] rounded-md text-sm text-[#FF6943] font-semibold">
                      {getInitials(item.customer_name)}
                    </div>

                    {/* NAME + PHONE */}
                    <div className="text-left">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.customer_name}
                      </p>
                      <p className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                        <a href={`tel:+${item.country_code}${item.mobile}`}>
                          <PhoneCall size={14} />
                        </a>
                        +{item.country_code} {""}
                        {item.mobile}
                      </p>
                    </div>
                  </div>

                  <ChevronRight
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      isOpen ? "rotate-90 text-orange-500" : ""
                    }`}
                  />
                </button>

                {/* ===== EXPANDABLE CONTENT ===== */}
                <div
                  className={`grid transition-all duration-300 ease-in-out ${
                    isOpen ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
                  }`}
                >
                  <div className="overflow-hidden px-4">
                    <div className="border-t border-gray-100 pt-4 pb-4 space-y-4">
                      {/* SUBJECT + MESSAGE */}
                      {item.subject && (
                        <div>
                          <p className="text-sm font-semibold text-gray-900">
                            {item.subject}
                          </p>
                          <p className="text-sm text-gray-600 mt-1 leading-relaxed">
                            {item.description}
                          </p>
                        </div>
                      )}

                      {/* MEAL INFO */}
                      {item.meal && (
                        <div className="flex gap-3 bg-gray-50 rounded-xl p-3">
                          <ImagePreview
                            src={item.meal.meal_cover_image}
                            alt={item.meal.meal_name}
                            className="h-14 w-14 rounded-lg object-cover"
                          />

                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {item.meal.meal_name}
                            </p>
                            <p className="text-xs text-gray-600 mt-0.5">
                              {item.description}
                            </p>
                          </div>
                        </div>
                      )}

                      {/* FOOTER */}
                      <div className="flex  justify-between gap-2 text-xs text-gray-500 pt-2 border-t">
                        <span className="flex items-center gap-1">
                          <Mail size={13} />
                          {item.email}
                        </span>
                        <span>{formatDateTime12(item.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="col-span-full flex justify-center items-center py-10 text-gray-400">
            <div className="text-center flex flex-col items-center space-y-3">
              <FilePenLine size={65} className="text-gray-300" />
            
              <h1 className="text-lg font-semibold text-gray-700">
                No inquiry Found
              </h1>
            </div>
          </div>
        )}
      </div>

      {/* <p className="text-sm text-gray-500 text-right mt-4">
        Showing page {current_page} of {total_pages} — {total_records} inquiries
      </p> */}

      {/* PAGINATION */}
      {allInquiry.length > 0 && total_pages > 1 && (
        <Pagination
          current={current_page}
          total={total_pages}
          onPageChange={setPage}
        />
      )}
    </section>
  );
};

export default Inquiry;
